import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { UserPlus } from "lucide-react";
import DomainElements from "@/components/gsa/DomainElements";
import { Domain } from "@/types/domain";
import GSANetworkStats from "@/components/gsa/GSANetworkStats";
import CircularDomainDial from "@/components/gsa/CircularDomainDial";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const domains: Domain[] = [
  {
    symbol: "Federal Procurement",
    name: "Federal Procurement and Contracting",
    categories: "Government-wide Acquisition Contracts (GWACs), Multiple Award Schedules (MAS), Simplified Acquisition Procedures (SAP), Federal Supply Schedules (FSS), Procurement Integrity and Compliance, Contractor Performance Evaluation"
  },
  {
    symbol: "Real Estate",
    name: "Real Estate and Public Buildings",
    categories: "Federal Property Management, Lease Administration and Negotiation, Sustainable Building Standards, Public Building Lifecycle Management, Historic Property Preservation, Space Optimization and Utilization"
  },
  {
    symbol: "IT",
    name: "IT and Digital Transformation",
    categories: "Cloud Adoption for Federal Agencies, Government-wide Cybersecurity Frameworks, AI and Automation in Federal Operations, Data Governance and Privacy in GSA, IT Modernization Initiatives, Digital Services Procurement"
  },
  {
    symbol: "Fleet",
    name: "Fleet Management and Transportation Services",
    categories: "Federal Vehicle Procurement, Electric and Hybrid Fleet Transition, GPS and Telematics for Government Vehicles, Federal Transportation Cost Optimization, Maintenance and Lifecycle Management, Public Sector Supply Chain Logistics"
  },
  {
    symbol: "Supply Chain",
    name: "Supply Chain and Logistics Management",
    categories: "Federal Supply Chain Resilience, Warehouse and Inventory Management, Strategic Sourcing for Government Agencies, Global Supply Chain Risk Mitigation, Vendor Management and Acquisition, Logistics and Distribution Optimization"
  },
  {
    symbol: "P3",
    name: "Public-Private Partnerships (p3)",
    categories: "Federal Infrastructure Investment Models, P3 Contracting and Compliance, Risk-sharing Mechanisms in Federal Projects, Alternative Funding for Federal Programs, Performance-based Contracting"
  },
  {
    symbol: "Workforce",
    name: "Federal Workforce Management",
    categories: "Human Capital Strategy in Federal Agencies, Federal Employee Training and Upskilling, DEI Initiatives and Compliance, Remote and Hybrid Work Policies, Leadership Development for Public Sector"
  },
  {
    symbol: "Energy",
    name: "Energy and Sustainability Initiatives",
    categories: "Green Procurement Policies, Net Zero Energy in Federal Buildings, Energy Performance Contracting (EPC), Climate Resilience in Federal Infrastructure, Renewable Energy Adoption in GSA"
  },
  {
    symbol: "Grants",
    name: "Federal Grants and Funding Programs",
    categories: "Grant Application and Compliance, Federal Economic Stimulus Packages, Disaster Recovery and FEMA Grants, Cooperative Agreements and Cost-sharing, Grant Program Impact Assessments"
  },
  {
    symbol: "Emergency",
    name: "Emergency Preparedness and Continuity of Operations",
    categories: "Federal Disaster Response Planning, Government Resilience Frameworks, Emergency Procurement Procedures, Cybersecurity and Infrastructure Resilience, Continuity of Operations (COOP) Planning"
  },
  {
    symbol: "Travel",
    name: "Federal Travel and Expense Management",
    categories: "Per Diem and Travel Reimbursement Guidelines, Federal Employee Travel Programs, Contracting for Travel Management Services, Travel Policy Compliance Audits, Government Conference and Event Planning"
  },
  {
    symbol: "Smart Cities",
    name: "Civil Smart Grid and Urban Innovation",
    categories: "Federal Smart City Strategies, IoT Applications for Public Infrastructure, Smart Energy Grid for Federal Buildings, Digital Identity Management for Public Services, Public Transportation Modernization"
  },
  {
    symbol: "Cybersecurity",
    name: "Federal Cybersecurity and Risk Management",
    categories: "Federal Data Protection Standards, Identity and Access Management (IAM), Continuous Monitoring and Threat Intelligence, Cloud Security for Federal Systems, Cyber Risk and Compliance for Government Vendors"
  },
  {
    symbol: "Vendor",
    name: "GSA Vendor and Industry Engagement",
    categories: "Industry Days and Federal Market Research, Vendor Prequalification and Certification, Small Business Contracting Strategies, GSA Schedule Pricing Analysis, Innovation Procurement Models"
  },
  {
    symbol: "Open Data",
    name: "Government Open Data and Open Access Publishing",
    categories: "Federal Data Sharing Agreements, Open Government Data Strategies, AI-driven Analytics for Public Services, Cloud-based Data Warehousing, GIS and Geospatial Intelligence for Public Use"
  }
];

const pbsDomains = [
  { name: "Design Automation", color: "bg-blue-600" },
  { name: "Space and Furniture Layout", color: "bg-blue-600" },
  { name: "Analysis Tools", color: "bg-blue-600" },
  { name: "Cost and Schedule Estimation", color: "bg-blue-600" },
  { name: "Visualization & 3D Rendering", color: "bg-blue-600" },
  { name: "Post-Occupancy Monitoring", color: "bg-blue-600" }
];

const networkStats = {
  totalParticipants: 1247,
  activeContributors: 543,
  knowledgeBases: 842,
  totalSolutions: 326,
  departments: [
    { name: "Office of the Administrator", participants: 89 },
    { name: "Federal Acquisition Service", participants: 347 },
    { name: "Public Buildings Service", participants: 298 },
    { name: "Office of the Chief Information Officer", participants: 127 },
    { name: "Office of Government-wide Policy", participants: 97 },
    { name: "Office of the General Counsel Overview", participants: 47 },
    { name: "Office of Strategic Communication", participants: 58 },
    { name: "Office of Congressional and Intergovernmental Affairs", participants: 35 },
    { name: "Office of Inspector General", participants: 33 },
    { name: "Office of Mission Assurance", participants: 31 },
    { name: "Office of the Chief Financial Officer", participants: 29 },
    { name: "Office of Administrative Services", participants: 27 },
    { name: "Office of Human Resources Management", participants: 26 },
    { name: "Office of Customer Experience", participants: 23 },
    { name: "Office of Small and Disadvantaged Business Utilization", participants: 23 }
  ]
};

const GsaChannel = () => {
  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <img 
                        src="/lovable-uploads/3e0cc269-0dd5-40b5-a1f0-0ebbe7e85733.png" 
                        alt="GSA Logo" 
                        className="w-16 h-16 object-contain cursor-pointer hover:scale-110 transition-transform" 
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>GSA Channel</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                <div>
                  <CardTitle className="text-2xl font-bold mb-1">
                    Welcome to the U.S. National Knowledge Network
                  </CardTitle>
                  <CardDescription className="text-base">
                    GSA Dashboard
                  </CardDescription>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/call-for-solution">
                        <Button 
                          className="bg-black text-white hover:bg-[#1e603b] transition-colors"
                          size="sm"
                        >
                          CfS
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Solution</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/call-for-destruction">
                        <Button 
                          className="bg-black text-white hover:bg-[#1e603b] transition-colors"
                          size="sm"
                        >
                          CfD
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Destruction</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Column 1: Network Stats (span 2) */}
              <div className="md:col-span-2">
                <GSANetworkStats 
                  totalParticipants={networkStats.totalParticipants}
                  activeContributors={networkStats.activeContributors}
                  knowledgeBases={networkStats.knowledgeBases}
                  totalSolutions={networkStats.totalSolutions}
                  departments={networkStats.departments}
                />
              </div>

              {/* Column 2: Ambassador & Template */}
              <div className="flex flex-col gap-4">
                <Card className="p-4 bg-blue-50">
                  <div className="flex items-center space-x-3 mb-3">
                    <Avatar className="h-12 w-12 border-2 border-blue-500">
                      <AvatarImage 
                        src="/lovable-uploads/5eb043bf-469d-46bf-abe8-2651aa4739b3.png" 
                        alt="Julia De Rosi" 
                        className="object-cover" 
                      />
                      <AvatarFallback className="bg-blue-100 text-blue-800 text-xl">JD</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-sm font-semibold">GSA Network Ambassador</h3>
                      <Link 
                        to="/julia-derosi-profile" 
                        className="text-primary hover:text-primary/80 font-medium text-sm"
                      >
                        Julia De Rosi
                      </Link>
                    </div>
                  </div>
                  <p className="text-xs text-gray-700 mb-1">
                    Former Deputy Assistant Commissioner, Design and Construction
                  </p>
                  <p className="text-xs text-gray-600 mb-3">
                    Leading the GSA knowledge network with 254 direct project managers and over 1,200 active participants across the organization.
                  </p>
                  <Button variant="outline" size="sm" className="w-full">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Join Knowledge Network
                  </Button>
                </Card>

                <a 
                  href="https://img1.wsimg.com/blobby/go/70e764c8-c764-4e83-ada3-5ccb3a393f3d/downloads/5add6269-a319-4fc0-9dcb-95a52a6c7b5a/iSPAI%20Industry_GSA.pdf?ver=1741886570881" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="transition-transform hover:scale-105 block"
                >
                  <Card className="p-3 bg-white">
                    <h4 className="text-sm font-semibold mb-2">iSPAI</h4>
                    <img 
                      src="/lovable-uploads/1055d4ec-0c8f-4663-81f4-ee6eed1dfc9c.png" 
                      alt="Industry Template: U.S. General Services Administration (GSA)" 
                      className="rounded-lg w-full object-contain border border-gray-200" 
                    />
                  </Card>
                </a>

                <CircularDomainDial domains={pbsDomains} />
              </div>
            </div>

            {/* GSA Domain Elements */}
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">GSA Domain Elements</h2>
              <DomainElements domains={domains} />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default GsaChannel;
